import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { closeCurrentPopin, displayPopin } from "../../Hook/popins";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import {
  api_getDailySpinGifts,
  api_getDailySpinSpin,
  dailyspinGifts_gift,
  dailyspinSpin,
} from "../../Api/dailyspin";
import Loader from "../../Component/Loader";
import { USER } from "../../Hook/user";
import useUser from "../../Hook/user";
import "./dailyspin.css";

type Gift = dailyspinGifts_gift & {
  color: string;
};

type SpinResult = {
  gift: Gift;
  spin: dailyspinSpin;
};

const DEFAULT_COLORS = [
  "#f97316",
  "#ec4899",
  "#a855f7",
  "#38bdf8",
  "#f87171",
  "#34d399",
  "#facc15",
  "#22c55e",
];

function DailySpin() {
  const intl = useIntl();
  const user = useUser();
  const display = displayPopin();
  const [spinning, setSpinning] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [result, setResult] = useState<SpinResult | null>(null);
  const [gifts, setGifts] = useState<Gift[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const wheelRef = useRef<HTMLDivElement>(null);
  const currentRotation = useRef(0);
  const canPlayDailySpin = user?.informations?.can_play_dailyspin !== false;

  const segmentAngle = gifts.length ? 360 / gifts.length : 0;
  const wheelRadius = 140;
  const centerRadius = 14;
  const labelRadius = (wheelRadius + centerRadius) / 2;

  useEffect(() => {
    let isMounted = true;
    api_getDailySpinGifts()
      .then((response) => {
        if (!isMounted) return;
        if (response.error_message || !response.gifts?.length) {
          setGifts([]);
          return;
        }
        setGifts(
          response.gifts.map((gift, index) => ({
            ...gift,
            color: gift.color ?? DEFAULT_COLORS[index % DEFAULT_COLORS.length],
          })),
        );
      })
      .catch(() => {
        if (!isMounted) return;
        setGifts([]);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const getWinnerId = (spinResponse: dailyspinSpin) => {
    if (typeof spinResponse.gift === "string") return spinResponse.gift;
    if (spinResponse.gift?.id) return spinResponse.gift.id;
    if (spinResponse.id) return spinResponse.id;
    return null;
  };

  const spin = useCallback(() => {
    if (spinning || gifts.length === 0 || !canPlayDailySpin) return;

    setResult(null);
    setError(null);
    setCountdown(3);
    setSpinning(true);
    const spinPromise = api_getDailySpinSpin().catch(
      () => ({ error_message: "dailyspin_spin_failed" }) as any,
    );

    let count = 3;
    const interval = setInterval(() => {
      count--;
      if (count > 0) {
        setCountdown(count);
      } else {
        clearInterval(interval);
        setCountdown(null);
        spinPromise.then((spinResponse: any) => {
          if (spinResponse?.error_message) {
            setError(spinResponse.error_message);
            setSpinning(false);
            return;
          }

          const winnerId = getWinnerId(spinResponse);
          if (!winnerId) {
            setError("dailyspin_spin_missing_gift");
            setSpinning(false);
            return;
          }

          const winnerIndex = gifts.findIndex((gift) => gift.id === winnerId);
          if (winnerIndex === -1) {
            setError("dailyspin_spin_unknown_gift");
            setSpinning(false);
            return;
          }

          const winner = gifts[winnerIndex];

          // The pointer is at top (0deg). Conic gradient segment i spans [i*angle, (i+1)*angle].
          // When wheel is rotated by R degrees, the point originally at Rdeg moves to the top.
          // So we need R % 360 === segmentMid to land that segment under the pointer.
          const extraSpins = 5 * 360;
          let delta = 360 - winnerIndex * segmentAngle - segmentAngle / 2;
          if (delta < 0) delta += 360;
          const targetRotation = currentRotation.current + extraSpins + delta;

          currentRotation.current = targetRotation;

          if (wheelRef.current) {
            wheelRef.current.style.transition =
              "transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99)";
            wheelRef.current.style.transform = `rotate(${targetRotation}deg)`;
          }

          setTimeout(() => {
            setSpinning(false);
            setResult({ gift: winner, spin: spinResponse });
            if (USER.informations) {
              USER.informations.can_play_dailyspin = false;
            }
          }, 4200);
        });
      }
    }, 1000);
  }, [canPlayDailySpin, gifts, segmentAngle, spinning]);

  const handleClaimPrize = () => {
    if (result?.spin.offer_code?.code) {
      display("subscribe", { code: result.spin.offer_code.code });
    }
    closeCurrentPopin();
  };

  const resultDetail = result?.spin.offer_code?.code
    ? intl.formatMessage(
        { id: "dailyspin_result__bonus_code" },
        { code: result.spin.offer_code.code },
      )
    : typeof result?.spin.days === "number"
      ? intl.formatMessage(
          { id: "dailyspin_result__days_unlocked" },
          { count: result.spin.days },
        )
      : typeof result?.spin.tokens === "number"
        ? intl.formatMessage(
            { id: "dailyspin_result__tokens_added" },
            { count: result.spin.tokens },
          )
        : null;

  return (
    <>
      <div
        onClick={closeCurrentPopin}
        className="top-0 left-0 right-0 overflow-hidden bottom-0 fixed bg-black opacity-60 z-40"
      />
      <div className="z-50 p-6 lg:max-w-md w-screen fixed max-h-[100dvh] overflow-hidden top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 lg:rounded-xl flex flex-col items-center gap-4 bg-gradient-to-b from-[#d946ef] to-[#fb923c]">
        <div
          onClick={closeCurrentPopin}
          className="absolute top-4 right-4 cursor-pointer text-white"
        >
          <FontAwesomeIcon icon={faClose} className="w-5 h-5" />
        </div>

        <h2 className="text-white text-xl font-bold text-center mt-2">
          {intl.formatMessage({ id: "dailyspin__title" })}
        </h2>
        <p className="text-white text-sm text-center opacity-90">
          {intl.formatMessage({ id: "dailyspin__subtitle" })}
        </p>

        {/* Wheel container */}
        <div className="relative w-[360px] h-[360px]">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center z-20 bg-black/30 rounded-full">
              <Loader />
            </div>
          )}
          {/* Pointer */}
          <div
            className="absolute overflow-hidden -top-3 left-1/2 -translate-x-1/2 z-10 w-0 h-0 drop-shadow-md"
            style={{
              borderLeft: "14px solid transparent",
              borderRight: "14px solid transparent",
              borderTop: "28px solid white",
            }}
          />

          {/* Wheel */}
          <div className="absolute -inset-[6px] rounded-full dailyspin-wheel-rim" />
          <div
            ref={wheelRef}
            className="relative w-full h-full rounded-full overflow-hidden dailyspin-wheel-core"
          >
            {/* Colored segments via conic gradient */}
            {gifts.length > 0 && (
              <div
                className="absolute inset-0 rounded-full z-[1]"
                style={{
                  background: `conic-gradient(${gifts
                    .map(
                      (gift, index) =>
                        `${gift.color} ${index * segmentAngle}deg ${(index + 1) * segmentAngle}deg`,
                    )
                    .join(", ")})`,
                }}
              />
            )}

            <div className="absolute inset-0 rounded-full dailyspin-wheel-vignette z-[2]" />

            {/* Labels layer */}
            {gifts.map((gift, index) => {
              const midAngle = index * segmentAngle + segmentAngle / 2;
              const midAngleInRadians = (midAngle * Math.PI) / 180;
              const radiusRatio = labelRadius / wheelRadius;
              const x = 50 + radiusRatio * 50 * Math.sin(midAngleInRadians);
              const y = 50 - radiusRatio * 50 * Math.cos(midAngleInRadians);
              const textRotation = midAngle - 90;
              const width = 120;

              return (
                <div
                  key={gift.id}
                  className="absolute z-[3]"
                  style={{
                    left: `${x}%`,
                    top: `${y}%`,
                    transform: `translate(-50%, -50%) rotate(${textRotation}deg)`,
                  }}
                >
                  <span
                    className="block text-[14px] ml-6 font-extrabold text-white uppercase text-center whitespace-normal leading-[1.05] [overflow-wrap:anywhere]"
                    style={{
                      width: `${width}px`,
                      textShadow: "1px 1px 2px rgba(0,0,0,0.4)",
                    }}
                  >
                    <FormattedMessage id={gift.id} />
                  </span>
                </div>
              );
            })}

            {/* Center circle */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full dailyspin-wheel-hub z-[6]">
              <div className="absolute inset-[8px] rounded-full bg-[#fdfdfd] opacity-95" />
            </div>
          </div>

          {/* Countdown overlay */}
          {countdown !== null && (
            <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
              <span
                className="text-[80px] font-black text-white dailyspin-pop"
                style={{ textShadow: "0 0 30px rgba(0,0,0,0.5)" }}
              >
                {countdown}
              </span>
            </div>
          )}

          {/* Floating result display */}
          {result && (
            <div className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col gap-2 top-1/2 z-30 w-[230px] rounded-2xl p-6 text-center dailyspin-reward bg-gradient-to-br from-[#35124c] to-[#6c1a87] shadow-[0_12px_24px_rgba(0,0,0,0.35)] border border-white/20">
              <p className="text-white font-extrabold text-[27px] leading-tight uppercase">
                <FormattedMessage id={result.gift.id} />
              </p>
              {resultDetail && (
                <p className="text-[12px] text-white/85 mt-1 leading-tight">
                  {resultDetail}
                </p>
              )}
              <button
                onClick={handleClaimPrize}
                className="mt-2 w-full rounded-full bg-gradient-to-r from-[#ff62b5] to-[#ff9f4d] text-[#3b1535] font-black py-2 text-[14px]"
              >
                {intl.formatMessage({ id: "dailyspin__claim" })}
              </button>
            </div>
          )}
        </div>

        {!result && !canPlayDailySpin && (
          <div className="bg-white rounded-lg p-3 w-full text-center text-black font-semibold">
            {intl.formatMessage({ id: "dailyspin__already_played" })}
          </div>
        )}

        {!result && error && (
          <div className="bg-white rounded-lg p-3 w-full text-center text-black font-semibold">
            {intl.formatMessage({ id: error })}
          </div>
        )}

        {/* Spin button */}
        {
          <button
            onClick={spin}
            disabled={
              !canPlayDailySpin ||
              spinning ||
              countdown !== null ||
              isLoading ||
              gifts.length === 0 ||
              Boolean(result)
            }
            className={`bg-white text-[#d946ef] font-extrabold text-lg uppercase py-3.5 px-12 rounded-full tracking-wide ${
              !canPlayDailySpin ||
              spinning ||
              countdown !== null ||
              isLoading ||
              gifts.length === 0
                ? "opacity-50 cursor-not-allowed"
                : "dailyspin-pulse"
            }`}
          >
            {intl.formatMessage({ id: "dailyspin__spin" })}
          </button>
        }
      </div>
    </>
  );
}

export default DailySpin;
